/**
 * AutoMapa analytics event taxonomy (Milestone 15).
 * Naming: lowercase_snake_case, action-oriented.
 *
 * Imports only from automapAnalyticsEvents (no buyerAnalytics runtime deps).
 */
import {
  BUYER_ANALYTICS_EVENTS,
  BUYER_EVENT_CATEGORIES,
  CHECKLIST_ANALYTICS_EVENTS,
  DEALER_CONTACT_ANALYTICS_EVENTS,
  FAIR_PRICE_ANALYTICS_EVENTS,
  LEAD_CAPTURE_ANALYTICS_EVENTS,
  RECOMMENDATION_ANALYTICS_EVENTS,
  RISK_ANALYTICS_EVENTS,
  SELLER_INTAKE_ANALYTICS_EVENTS,
} from "../constants/automapAnalyticsEvents";

export { BUYER_ANALYTICS_EVENTS, BUYER_EVENT_CATEGORIES };

/** Page / view */
export const PAGE_EVENTS = {
  PAGE_VIEW: "page_view",
  PAGE_VIEWED: BUYER_ANALYTICS_EVENTS.PAGE_VIEWED,
};

/** Search results */
export const SEARCH_EVENTS = {
  VIEWED: "vehicle_search_viewed",
  SUBMITTED: "vehicle_search_submitted",
  FILTER_CHANGED: "vehicle_search_filter_changed",
  FILTERS_APPLIED: "vehicle_search_filters_applied",
  FILTERS_CLEARED: "vehicle_search_filters_cleared",
  SORT_CHANGED: "vehicle_search_sort_changed",
  PAGE_CHANGED: "vehicle_search_page_changed",
  LOAD_MORE_CLICKED: "vehicle_search_load_more_clicked",
  NO_RESULTS: "vehicle_search_no_results",
  ERROR: "vehicle_search_error",
  RESULT_CLICKED: "vehicle_search_result_clicked",
  FILTERS_OPENED: "vehicle_search_filters_opened",
  SEARCH_PERFORMED: BUYER_ANALYTICS_EVENTS.SEARCH_PERFORMED,
  SEARCH_RESULTS_VIEWED: BUYER_ANALYTICS_EVENTS.SEARCH_RESULTS_VIEWED,
};

/** Search cards */
export const CARD_EVENTS = {
  VIEWED: "vehicle_card_viewed",
  CLICKED: "vehicle_card_clicked",
  DETAIL_CLICKED: "vehicle_card_detail_clicked",
  WHATSAPP_CLICKED: "vehicle_card_whatsapp_clicked",
  SAVE_CLICKED: "vehicle_card_save_clicked",
  COMPARE_CLICKED: "vehicle_card_compare_clicked",
  DEALER_CLICKED: "vehicle_card_dealer_clicked",
  FAIR_PRICE_BADGE_CLICKED: "vehicle_card_fair_price_badge_clicked",
  RISK_BADGE_CLICKED: "vehicle_card_risk_badge_clicked",
};

/** Vehicle detail */
export const DETAIL_EVENTS = {
  VIEWED: BUYER_ANALYTICS_EVENTS.VEHICLE_DETAIL_VIEWED,
  GALLERY_INTERACTED: "vehicle_detail_gallery_interacted",
  CONTACT_PANEL_VIEWED: "vehicle_detail_contact_panel_viewed",
  MOBILE_CTA_CLICKED: "vehicle_detail_mobile_cta_clicked",
  CHECKLIST_CLICKED: "vehicle_detail_checklist_clicked",
  SIMILAR_CLICKED: "vehicle_detail_similar_clicked",
  SHARE_CLICKED: "vehicle_detail_share_clicked",
  IMAGE_CLICKED: "vehicle_detail_image_clicked",
  COPY_LINK_CLICKED: "vehicle_detail_copy_link_clicked",
  SPECS_VIEWED: "vehicle_detail_specs_viewed",
  UNAVAILABLE_VIEWED: "vehicle_detail_unavailable_viewed",
  BACK_TO_SEARCH_CLICKED: "vehicle_detail_back_to_search_clicked",
};

/** Fair price, risk, checklist, dealer, lead, recommendation, map, share */
export const FAIR_PRICE_EVENTS = FAIR_PRICE_ANALYTICS_EVENTS;
export const RISK_EVENTS = RISK_ANALYTICS_EVENTS;
export const CHECKLIST_EVENTS = CHECKLIST_ANALYTICS_EVENTS;
export const DEALER_EVENTS = DEALER_CONTACT_ANALYTICS_EVENTS;
export const LEAD_EVENTS = {
  CREATED: "vehicle_lead_created",
  CREATE_FAILED: "vehicle_lead_create_failed",
  DUPLICATE_POSSIBLE: "vehicle_lead_duplicate_possible",
  WHATSAPP_OPENED: "vehicle_lead_whatsapp_opened",
  PHONE_OPENED: "vehicle_lead_phone_opened",
  FORM_OPENED: BUYER_ANALYTICS_EVENTS.LEAD_FORM_OPENED,
  FORM_SUBMITTED: BUYER_ANALYTICS_EVENTS.LEAD_FORM_SUBMITTED,
  LEGACY_CREATED: BUYER_ANALYTICS_EVENTS.LEAD_CREATED,
  ...LEAD_CAPTURE_ANALYTICS_EVENTS,
};

export const RECOMMENDATION_EVENTS = RECOMMENDATION_ANALYTICS_EVENTS;

export const MAP_LIST_EVENTS = {
  VIEW_MODE_CHANGED: "search_view_mode_changed",
  MAP_OPENED: "search_map_opened",
  MARKER_CLICKED: "search_map_marker_clicked",
  POPUP_DETAIL_CLICKED: "search_map_popup_detail_clicked",
  CARD_HIGHLIGHTED_FROM_MAP: "search_card_highlighted_from_map",
  MAP_ERROR: "search_map_error",
  NO_COORDINATES: "search_map_no_coordinates",
};

export const SHARE_EVENTS = {
  VEHICLE_SHARE_CLICKED: "vehicle_share_clicked",
  VEHICLE_COPY_LINK_CLICKED: "vehicle_copy_link_clicked",
  CHECKLIST_SHARE_CLICKED: "checklist_share_clicked",
  SEO_LANDING_PAGE_VIEWED: BUYER_ANALYTICS_EVENTS.SEO_PAGE_VIEWED,
};

/** FX / display currency (Milestone 17) */
export const FX_CURRENCY_EVENTS = {
  CURRENCY_CHANGED: "automap_currency_changed",
  FX_SNAPSHOT_VIEWED: "automap_fx_snapshot_viewed",
};

/** Partner portal inventory rollout (Milestone 12) */
export const PARTNER_INVENTORY_EVENTS = {
  VIEW_LOADED: "partner_inventory_view_loaded",
  VIEW_TOGGLED: "partner_inventory_view_toggled",
  GROUPED_LOADED: "partner_grouped_inventory_loaded",
  GROUPED_ERROR: "partner_grouped_inventory_error",
  NORMALIZATION_REVIEW_OPENED: "partner_normalization_review_opened",
  MANUAL_NORMALIZE_APPLIED: "partner_manual_normalize_applied",
  BULK_NORMALIZE_STARTED: "partner_bulk_normalize_started",
  BULK_NORMALIZE_COMPLETED: "partner_bulk_normalize_completed",
  VEHICLE_IDENTITY_HEALTH_VIEWED: "partner_vehicle_identity_health_viewed",
};

export const AUTOMAPA_ANALYTICS_EVENTS = {
  ...PAGE_EVENTS,
  ...SEARCH_EVENTS,
  ...CARD_EVENTS,
  ...DETAIL_EVENTS,
  ...FAIR_PRICE_EVENTS,
  ...RISK_EVENTS,
  ...CHECKLIST_EVENTS,
  ...DEALER_EVENTS,
  ...LEAD_EVENTS,
  ...RECOMMENDATION_EVENTS,
  ...MAP_LIST_EVENTS,
  ...SHARE_EVENTS,
  ...FX_CURRENCY_EVENTS,
  ...PARTNER_INVENTORY_EVENTS,
  ...BUYER_ANALYTICS_EVENTS,
};

export const EVENT_CATEGORY_MAP = {
  page_view: BUYER_EVENT_CATEGORIES.PAGE_VIEW,
  vehicle_search_viewed: BUYER_EVENT_CATEGORIES.SEARCH,
  vehicle_search_submitted: BUYER_EVENT_CATEGORIES.SEARCH,
  vehicle_search_error: BUYER_EVENT_CATEGORIES.SEARCH,
  vehicle_search_result_clicked: BUYER_EVENT_CATEGORIES.SEARCH,
  vehicle_card_viewed: BUYER_EVENT_CATEGORIES.LISTING_ENGAGEMENT,
  vehicle_card_clicked: BUYER_EVENT_CATEGORIES.SEARCH,
  vehicle_detail_unavailable_viewed: BUYER_EVENT_CATEGORIES.LISTING_ENGAGEMENT,
  vehicle_lead_created: BUYER_EVENT_CATEGORIES.LEAD,
  vehicle_lead_create_failed: BUYER_EVENT_CATEGORIES.LEAD,
  vehicle_recommendation_card_clicked: BUYER_EVENT_CATEGORIES.RECOMMENDATION,
  fair_price_badge_clicked: BUYER_EVENT_CATEGORIES.FAIR_PRICE,
  vehicle_risk_badge_clicked: BUYER_EVENT_CATEGORIES.RISK,
  buyer_checklist_item_clicked: BUYER_EVENT_CATEGORIES.CHECKLIST,
  vehicle_whatsapp_clicked: BUYER_EVENT_CATEGORIES.CONTACT,
  vehicle_contact_whatsapp_opened: BUYER_EVENT_CATEGORIES.CONTACT,
  search_view_mode_changed: BUYER_EVENT_CATEGORIES.SEARCH,
  lead_modal_opened: BUYER_EVENT_CATEGORIES.LEAD,
  lead_form_started: BUYER_EVENT_CATEGORIES.LEAD,
  lead_form_field_error: BUYER_EVENT_CATEGORIES.LEAD,
  lead_form_submitted: BUYER_EVENT_CATEGORIES.LEAD,
  lead_submission_succeeded: BUYER_EVENT_CATEGORIES.LEAD,
  lead_submission_failed: BUYER_EVENT_CATEGORIES.LEAD,
  lead_submission_duplicate: BUYER_EVENT_CATEGORIES.LEAD,
  lead_submission_rate_limited: BUYER_EVENT_CATEGORIES.LEAD,
  lead_submission_spam_rejected: BUYER_EVENT_CATEGORIES.LEAD,
  lead_modal_closed: BUYER_EVENT_CATEGORIES.LEAD,
  lead_retry_clicked: BUYER_EVENT_CATEGORIES.LEAD,
  seller_intake_page_viewed: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_primary_cta_clicked: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_secondary_cta_clicked: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_form_started: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_field_changed: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_validation_error: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_submit_attempted: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_submit_succeeded: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_submit_failed: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_duplicate_detected: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_spam_rejected: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_rate_limited: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_reset_clicked: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  seller_intake_form_abandoned: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
  automap_currency_changed: BUYER_EVENT_CATEGORIES.FILTER,
  automap_fx_snapshot_viewed: BUYER_EVENT_CATEGORIES.PAGE_VIEW,
  [SELLER_INTAKE_ANALYTICS_EVENTS.PAGE_VIEWED]: BUYER_EVENT_CATEGORIES.SELLER_INTAKE,
};

export function resolveAutomapEventCategory(eventName) {
  return EVENT_CATEGORY_MAP[eventName] || null;
}
